import React, { createContext, useState, useEffect, useContext, ReactNode } from 'react';
import { useDataProvider, useNotify, useRefresh, useRedirect } from 'react-admin';

export interface Tenant {
  code: string;
  name: string | null;
  description: string | null;
  logoFileId: number | null;
  logoThumbnail: string | null;
  logoUrl: string | null;
  createdAt: Date | null;
  updatedAt: Date | null;
  isDefault?: boolean;
}

interface TenantContextType {
  availableTenants: Tenant[];
  currentTenant: Tenant | null;
  loading: boolean;
  error: string | null;
  switchTenant: (tenantCode: string) => void;
  refreshTenants: () => Promise<void>;
}

const TenantContext = createContext<TenantContextType | undefined>(undefined);

export const TenantProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [availableTenants, setAvailableTenants] = useState<Tenant[]>([]);
  const [currentTenant, setCurrentTenant] = useState<Tenant | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const refresh = useRefresh();
  const redirect = useRedirect();

  const getCurrentUserId = (): number | null => {
    const userJson = localStorage.getItem('user');
    if (userJson) {
      try {
        const user = JSON.parse(userJson);
        return user.id;
      } catch (e) {
        console.error('Error parsing user JSON:', e);
      }
    }
    return null;
  };

  const fetchAvailableTenants = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const baseUrl = import.meta.env.VITE_SIMPLE_REST_URL;
      const accessToken = localStorage.getItem('accessToken');
      
      if (!accessToken) {
        console.error('No access token found');
        setError('Authentication required');
        setLoading(false);
        return;
      }

      // Fetch tenants that the user has access to
      const response = await fetch(`${baseUrl}/tenants`, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch tenants: ${response.statusText}`);
      }

      const fetchedTenants = await response.json();
      console.log('Fetched tenants from API:', fetchedTenants);
      
      // Ensure tenants is an array
      const tenants = Array.isArray(fetchedTenants) ? fetchedTenants : 
                      (fetchedTenants.data ? fetchedTenants.data : []);
      
      setAvailableTenants(tenants);
      
      // Check if there's a stored current tenant
      const storedTenantJson = localStorage.getItem('currentTenant');
      let currentTenant = null;
      
      if (storedTenantJson) {
        try {
          const storedTenant = JSON.parse(storedTenantJson);
          currentTenant = tenants.find((t: Tenant) => t.code === storedTenant.code);
        } catch (e) {
          console.error('Error parsing stored tenant:', e);
        }
      }
      
      // If no stored tenant or stored tenant not found, use first available tenant
      if (!currentTenant && tenants.length > 0) {
        // Try to find a default tenant or use the first one
        currentTenant = tenants.find((t: Tenant) => t.isDefault) || tenants[0];
      }
      
      if (currentTenant) {
        setCurrentTenant(currentTenant);
        localStorage.setItem('currentTenant', JSON.stringify(currentTenant));
        console.log('Set current tenant:', currentTenant);
      } else {
        console.warn('No tenants available for the current user');
        setError('No tenants available');
      }

    } catch (error) {
      console.error('Error fetching tenants:', error);
      setError(error instanceof Error ? error.message : 'Failed to fetch tenants');
      
      // Fallback to hardcoded tenants if API fails
      console.log('Falling back to hardcoded tenants due to API error');
      const fallbackTenants: Tenant[] = [
        {
          code: 'dm',
          name: 'Distill Mobile',
          description: 'Distill Mobile tenant',
          logoFileId: null,
          logoThumbnail: null,
          logoUrl: null,
          createdAt: null,
          updatedAt: null,
          isDefault: true
        },
        {
          code: 'dt',
          name: 'Distill Trucking',
          description: 'Distill Trucking tenant',
          logoFileId: null,
          logoThumbnail: null,
          logoUrl: null,
          createdAt: null,
          updatedAt: null,
          isDefault: false
        }
      ];
      
      setAvailableTenants(fallbackTenants);
      
      // Set default tenant
      const defaultTenant = fallbackTenants.find(t => t.isDefault) || fallbackTenants[0];
      setCurrentTenant(defaultTenant);
      localStorage.setItem('currentTenant', JSON.stringify(defaultTenant));
    } finally {
      setLoading(false);
    }
  };

  const switchTenant = (tenantCode: string) => {
    const tenant = availableTenants.find(t => t.code === tenantCode);
    if (tenant) {
      const previousTenant = currentTenant;
      setCurrentTenant(tenant);
      // Persist current tenant to localStorage for the data provider
      localStorage.setItem('currentTenant', JSON.stringify(tenant));
      
      // Only refresh and redirect if we're actually switching to a different tenant
      if (previousTenant && previousTenant.code !== tenant.code) {
        console.log(`Switching from ${previousTenant.name} to ${tenant.name}, redirecting to dashboard...`);
        // Invalidate react-admin queries before routing to avoid stale data.
        refresh();
        // Redirect to the dashboard so data reloads with the new tenant context
        redirect('/');
      }
      
      if (notify) {
        notify(`Switched to tenant: ${tenant.name || tenant.code}`, { type: 'success' });
      }
    } else {
      if (notify) {
        notify('Invalid tenant selection', { type: 'error' });
      }
    }
  };

  const refreshTenants = async () => {
    await fetchAvailableTenants();
  };

  useEffect(() => {
    if (dataProvider) {
      fetchAvailableTenants();
    }
  }, [dataProvider]);

  return (
    <TenantContext.Provider
      value={{
        availableTenants,
        currentTenant,
        loading,
        error,
        switchTenant,
        refreshTenants
      }}
    >
      {children}
    </TenantContext.Provider>
  );
};

export const useTenant = () => {
  const context = useContext(TenantContext);
  if (context === undefined) {
    throw new Error('useTenant must be used within a TenantProvider');
  }
  return context;
};